import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from 'app/config/service.config';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    throwError,
} from 'rxjs';
import { LogService } from '../logging/log.service';
import { PaymentMethod, PaymentStatus } from '../payment/payment.types';
import { ProductType } from '../product/product.types';
import {
    ExportJobResponse,
    OrderCompletionRequest,
    OrderPaidRequest,
    OrderRequest,
    OrderResponse,
    OrderSource,
    OrderStatus,
    OrderType,
    Pagination,
    SimActivationRetryRequest,
} from './order.types';

@Injectable({ providedIn: 'root' })
export class OrderService {
    private _httpClient = inject(HttpClient);
    private _apiServer = inject(AppConfig);
    private _logging = inject(LogService);
    private apiUrl = this._apiServer.settings.apiServer.eastelService;

    private _orders: BehaviorSubject<OrderResponse[]> = new BehaviorSubject<
        OrderResponse[]
    >([]);
    private _ongoingOrders: BehaviorSubject<OrderResponse[]> =
        new BehaviorSubject<OrderResponse[]>([]);
    private _pagination: BehaviorSubject<Pagination> =
        new BehaviorSubject<Pagination>(null);
    private _ongoingPagination: BehaviorSubject<Pagination> =
        new BehaviorSubject<Pagination>(null);
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for orders
     */
    get orders$(): Observable<OrderResponse[]> {
        return this._orders.asObservable();
    }

    /**
     * Getter for ongoing orders
     */
    get ongoingOrders$(): Observable<OrderResponse[]> {
        return this._ongoingOrders.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for ongoing orders pagination
     */
    get ongoingPagination$(): Observable<Pagination> {
        return this._ongoingPagination.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get order details by order number (e.g. ORD-20250213-0001)
     * @param orderNumber Order number to retrieve
     * @returns Order details
     */
    getOrderByOrderNumber(orderNumber: string): Observable<OrderResponse> {
        return this._httpClient
            .get<OrderResponse>(
                `${this.apiUrl}/orders/order-number/${orderNumber}`
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service getOrderByOrderNumber error',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service getOrderByOrderNumber',
                            response
                        );
                    }
                    return response;
                })
            );
    }

    retrySimActivationSuccess(
        request: SimActivationRetryRequest
    ): Observable<any> {
        return this._httpClient
            .post(`${this.apiUrl}/orders/sim-activation/retry`, request)
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Error retrying SIM activation success',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Get orders
     * @param params - The parameters for the orders
     * @returns The orders
     */
    getOrders(
        params: {
            page: number;
            size: number;
            sortBy: string;
            sortOrder: string;
            orderSource: OrderSource[] | null;
            fromDate: string;
            toDate: string;
            paymentStatus: PaymentStatus[] | null;
            paymentMethod: PaymentMethod[] | null;
            globalSearch: string | null;
            orderNumber: string | null;
            productName: string | null;
            productSku: string | null;
            orderStatus: OrderStatus[] | null;
            orderType: OrderType[] | null;
            parcelDailyShipmentOnly?: boolean;
        } = {
            page: 0,
            size: 10,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            orderSource: null,
            fromDate: null,
            toDate: null,
            paymentStatus: null,
            paymentMethod: null,
            globalSearch: null,
            orderNumber: null,
            productName: null,
            productSku: null,
            orderStatus: null,
            orderType: null,
            parcelDailyShipmentOnly: false,
        }
    ) {
        // Delete empty value
        Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
                params[key] = params[key].filter((element) => element !== null);
            }
            if (
                params[key] === null ||
                params[key] === undefined ||
                params[key] === '' ||
                (Array.isArray(params[key]) && params[key].length === 0)
            ) {
                delete params[key];
            }
        });

        const header = {
            params,
        };

        return this._httpClient.get(`${this.apiUrl}/orders/page`, header).pipe(
            catchError(() => {
                return of(null);
            }),
            map((response: any) => {
                if (response) {
                    this._logging.debug('Order Service getOrders', response);
                    this._orders.next(response.content);

                    const pagination = {
                        length: response.totalElements,
                        size: response.size,
                        page: response.page,
                        lastPage: response.totalPages,
                        startIndex: response.page * response.size, // Calculate start index
                        endIndex: Math.min(
                            response.page * response.size + response.size - 1,
                            response.totalElements - 1
                        ),
                    };
                    this._pagination.next(pagination);
                    return response.content;
                }
                return [];
            })
        );
    }

    /**
     * Get ongoing orders (orders that need processing)
     * @param params - The parameters for the orders
     * @returns The ongoing orders
     */
    getOngoingOrders(
        params: {
            page: number;
            size: number;
            sortBy: string;
            sortOrder: string;
            orderSource: OrderSource[] | null;
            fromDate: string | null;
            toDate: string | null;
            globalSearch: string | null;
            orderNumber: string | null;
            productName: string | null;
            productSku: string | null;
            paymentStatus: PaymentStatus[] | null;
            paymentMethod: PaymentMethod[] | null;
            orderStatus?: OrderStatus[] | null;
            parcelDailyShipmentOnly?: boolean;
            excludeProductTypes?: ProductType[] | null;
        } = {
            page: 0,
            size: 10,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            orderSource: null,
            fromDate: null,
            toDate: null,
            globalSearch: null,
            orderNumber: null,
            productName: null,
            productSku: null,
            paymentStatus: null,
            paymentMethod: null,
            parcelDailyShipmentOnly: false,
            excludeProductTypes: null,
        }
    ) {
        const ongoingStatuses =
            params.orderStatus?.length
                ? params.orderStatus
                : [
                      OrderStatus.PENDING,
                      OrderStatus.PROCESSING,
                      OrderStatus.PAYMENT_PENDING,
                      OrderStatus.PAYMENT_COMPLETED,
                  ];

        // Delete empty value
        Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
                params[key] = params[key].filter((element) => element !== null);
            }
            if (
                params[key] === null ||
                params[key] === undefined ||
                params[key] === '' ||
                (Array.isArray(params[key]) && params[key].length === 0)
            ) {
                delete params[key];
            }
        });

        const requestParams = {
            ...params,
            orderStatus: ongoingStatuses,
            orderType: OrderType.CUSTOMER_PURCHASE,
        };

        const header = {
            params: requestParams,
        };

        return this._httpClient.get(`${this.apiUrl}/orders/page`, header).pipe(
            catchError(() => {
                return of(null);
            }),
            map((response: any) => {
                if (response) {
                    this._logging.debug(
                        'Order Service getOngoingOrders',
                        response
                    );
                    this._ongoingOrders.next(response.content);

                    const pagination = {
                        length: response.totalElements,
                        size: response.size,
                        page: response.page,
                        lastPage: response.totalPages,
                        startIndex: response.page * response.size, // Calculate start index
                        endIndex: Math.min(
                            response.page * response.size + response.size - 1,
                            response.totalElements - 1
                        ),
                    };
                    this._ongoingPagination.next(pagination);
                    return response.content;
                }
                return [];
            })
        );
    }

    /**
     * Get order details by ID
     * @param orderId Order ID to retrieve
     * @returns Order details
     */
    getOrderById(orderId: string): Observable<OrderResponse> {
        return this._httpClient
            .get<OrderResponse>(`${this.apiUrl}/orders/${orderId}`)
            .pipe(
                catchError(() => {
                    return of(null);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service getOrderById',
                            response
                        );
                        return response;
                    }
                    return null;
                })
            );
    }

    /**
     * Mark order as completed
     * @param orderNumber Order Number
     * @returns Updated order
     */
    markOrderAsCompleted(
        orderNumber: string,
        orderCompletionRequest: OrderCompletionRequest
    ): Observable<OrderResponse> {
        return this._httpClient
            .post<OrderResponse>(
                `${this.apiUrl}/orders/${orderNumber}/complete`,
                orderCompletionRequest
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service markOrderAsCompleted error',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service markOrderAsCompleted',
                            response
                        );

                        // Update the orders in both BehaviorSubjects
                        this._updateOrderInSubjects(response);

                        return response;
                    }
                    return null;
                })
            );
    }

    /**
     * Mark order as paid
     * @param orderNumber Order Number
     * @returns Updated order
     */
    markOrderAsPaid(
        orderNumber: string,
        orderPaidRequest: OrderPaidRequest
    ): Observable<OrderResponse> {
        return this._httpClient
            .post<OrderResponse>(
                `${this.apiUrl}/orders/${orderNumber}/paid`,
                orderPaidRequest
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service markOrderAsPaid error',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service markOrderAsPaid',
                            response
                        );

                        // Update the orders in both BehaviorSubjects
                        this._updateOrderInSubjects(response);

                        return response;
                    }
                    return null;
                })
            );
    }

    /**
     * Cancel order
     * @param orderNumber Order Number
     * @param cancelRequest Cancel request data
     * @returns Updated order
     */
    cancelOrder(
        orderNumber: string,
        cancelRequest: any
    ): Observable<OrderResponse> {
        return this._httpClient
            .post<OrderResponse>(
                `${this.apiUrl}/orders/${orderNumber}/cancel`,
                cancelRequest
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service cancelOrder error',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service cancelOrder',
                            response
                        );

                        // Update the orders in both BehaviorSubjects
                        this._updateOrderInSubjects(response);

                        return response;
                    }
                    return null;
                })
            );
    }

    /**
     * Create order
     */
    createOrder(order: OrderRequest): Observable<OrderResponse> {
        return this._httpClient
            .post<OrderResponse>(`${this.apiUrl}/orders`, order)
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service createOrder error',
                        error
                    );
                    return throwError(() => error);
                }),
                map((response: OrderResponse) => {
                    if (response) {
                        this._logging.debug(
                            'Order Service createOrder',
                            response
                        );
                        // Get current orders and append the new one
                        let currentOrders: OrderResponse[] = [];
                        this._orders
                            .subscribe((orders) => {
                                currentOrders = orders;
                            })
                            .unsubscribe();
                        this._orders.next([...currentOrders, response]);

                        // Get current ongoing orders and append the new one
                        let ongoingOrders: OrderResponse[] = [];
                        this._ongoingOrders
                            .subscribe((orders) => {
                                ongoingOrders = orders;
                            })
                            .unsubscribe();
                        this._ongoingOrders.next([...ongoingOrders, response]);

                        // Update ongoing pagination
                        const currentPagination =
                            this._ongoingPagination.getValue();
                        if (currentPagination) {
                            this._ongoingPagination.next({
                                ...currentPagination,
                                length: currentPagination.length + 1,
                                lastPage: Math.ceil(
                                    (currentPagination.length + 1) /
                                        currentPagination.size
                                ),
                            });
                        }

                        return response;
                    }
                    return null;
                })
            );
    }

    /**
     * Download invoice PDF for an order
     */
    downloadInvoicePdf(orderNumber: string): Observable<Blob> {
        return this._httpClient
            .get(`${this.apiUrl}/orders/${orderNumber}/pdf/invoice`, {
                responseType: 'blob',
            })
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service downloadInvoicePdf error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Private method to update an order in both order BehaviorSubjects
     */
    private _updateOrderInSubjects(updatedOrder: OrderResponse): void {
        // Update in regular orders
        const currentOrders = this._orders.getValue();
        const orderIndex = currentOrders.findIndex(
            (order) => order.id === updatedOrder.id
        );

        if (orderIndex !== -1) {
            const updatedOrders = [...currentOrders];
            updatedOrders[orderIndex] = updatedOrder;
            this._orders.next(updatedOrders);
        }

        // Update in ongoing orders (or remove if no longer ongoing)
        const currentOngoingOrders = this._ongoingOrders.getValue();
        const ongoingOrderIndex = currentOngoingOrders.findIndex(
            (order) => order.id === updatedOrder.id
        );

        if (ongoingOrderIndex !== -1) {
            const updatedOngoingOrders = [...currentOngoingOrders];

            // Check if the order should still be in the ongoing list
            const ongoingStatuses = [
                OrderStatus.PENDING,
                OrderStatus.PROCESSING,
                OrderStatus.PAYMENT_PENDING,
                OrderStatus.PAYMENT_COMPLETED,
            ];

            if (
                ongoingStatuses.includes(
                    updatedOrder.orderStatus as OrderStatus
                )
            ) {
                updatedOngoingOrders[ongoingOrderIndex] = updatedOrder;
            } else {
                // Remove from ongoing if status changed to non-ongoing
                updatedOngoingOrders.splice(ongoingOrderIndex, 1);

                // Update ongoing pagination
                const currentPagination = this._ongoingPagination.getValue();
                if (currentPagination) {
                    this._ongoingPagination.next({
                        ...currentPagination,
                        length: currentPagination.length - 1,
                        lastPage: Math.ceil(
                            (currentPagination.length - 1) /
                                currentPagination.size
                        ),
                    });
                }
            }

            this._ongoingOrders.next(updatedOngoingOrders);
        }
    }

    /**
     * Request async Excel export
     */
    requestAsyncExport(params: any): Observable<ExportJobResponse> {
        // Delete empty value
        Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
                params[key] = params[key].filter((element) => element !== null);
            }
            if (
                params[key] === null ||
                params[key] === undefined ||
                params[key] === '' ||
                (Array.isArray(params[key]) && params[key].length === 0)
            ) {
                delete params[key];
            }
        });

        const header = {
            params,
        };

        return this._httpClient
            .post<ExportJobResponse>(
                `${this.apiUrl}/orders/export/xlsx/async`,
                null,
                header
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service requestAsyncExport error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Poll async export job status
     */
    getExportJobStatus(jobId: string): Observable<ExportJobResponse> {
        return this._httpClient
            .get<ExportJobResponse>(
                `${this.apiUrl}/orders/export/xlsx/jobs/${jobId}`
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service getExportJobStatus error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Download completed async export
     */
    downloadExportFile(jobId: string): Observable<Blob> {
        return this._httpClient
            .get(`${this.apiUrl}/orders/export/xlsx/jobs/${jobId}/download`, {
                responseType: 'blob',
            })
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service downloadExportFile error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Request async order statement Excel export
     */
    requestAsyncStatementExport(params: any): Observable<ExportJobResponse> {
        Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
                params[key] = params[key].filter((element) => element !== null);
            }
            if (
                params[key] === null ||
                params[key] === undefined ||
                params[key] === '' ||
                (Array.isArray(params[key]) && params[key].length === 0)
            ) {
                delete params[key];
            }
        });

        return this._httpClient
            .post<ExportJobResponse>(
                `${this.apiUrl}/orders/statement/xlsx/async`,
                null,
                { params }
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service requestAsyncStatementExport error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Poll async order statement export job status
     */
    getStatementExportJobStatus(jobId: string): Observable<ExportJobResponse> {
        return this._httpClient
            .get<ExportJobResponse>(
                `${this.apiUrl}/orders/statement/xlsx/jobs/${jobId}`
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service getStatementExportJobStatus error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Download completed async order statement export
     */
    downloadStatementExportFile(jobId: string): Observable<Blob> {
        return this._httpClient
            .get(
                `${this.apiUrl}/orders/statement/xlsx/jobs/${jobId}/download`,
                {
                    responseType: 'blob',
                }
            )
            .pipe(
                catchError((error) => {
                    this._logging.error(
                        'Order Service downloadStatementExportFile error',
                        error
                    );
                    return throwError(() => error);
                })
            );
    }
}
